/**
 * Consolidated Settings Page for CampusOS
 * Owner-only access with three tabs: General, Permissions, Notifications
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader, EmptyState, BadgePill } from '@/components/campus';
import {
  Settings,
  Building2,
  Shield,
  Bell,
  ImagePlus,
  Loader2,
  Check,
  Lock,
  Info,
  Link2,
  Instagram,
  Twitter,
  Globe,
  Users,
  Crown,
  Calendar,
  DollarSign,
  FileText,
  MessageSquare,
  Key,
  TestTube,
  AlertCircle,
  Mail,
  Clock,
  Briefcase,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== SHARED COMPONENTS ====================

const SavingIndicator = ({ saving, saved }) => {
  if (saving) {
    return (
      <div className="flex items-center gap-1.5 text-electric-ice-blue">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span className="text-xs">Saving...</span>
      </div>
    );
  }
  if (saved) {
    return (
      <div className="flex items-center gap-1.5 text-cool-mint">
        <Check className="w-3.5 h-3.5" />
        <span className="text-xs">Saved</span>
      </div>
    );
  }
  return null;
};

const SettingsSection = ({ title, description, icon: Icon, children }) => (
  <div className="bg-graphite/50 rounded-2xl border border-white/[0.06] overflow-hidden">
    <div className="p-5 border-b border-white/[0.06]">
      <div className="flex items-center gap-3">
        {Icon && (
          <div className="w-10 h-10 rounded-xl bg-electric-ice-blue/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-electric-ice-blue" />
          </div>
        )}
        <div>
          <h3 className="font-semibold text-cloud-white">{title}</h3>
          {description && <p className="text-sm text-muted-text mt-0.5">{description}</p>}
        </div>
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const AutoSaveInput = ({ label, value, onChange, onSave, type = 'text', placeholder, multiline = false, disabled = false }) => {
  const [localValue, setLocalValue] = useState(value || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleBlur = async () => {
    if (localValue !== value && !disabled) {
      setSaving(true);
      await onSave(localValue);
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
  };

  const InputComponent = multiline ? Textarea : Input;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-soft-silver">{label}</Label>
        <SavingIndicator saving={saving} saved={saved} />
      </div>
      <InputComponent
        type={type}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className="bg-obsidian border-white/[0.08] text-cloud-white disabled:opacity-50"
      />
    </div>
  );
};

// ==================== GENERAL TAB ====================

const OFFICER_TITLE_CONFIG = {
  president: { label: 'President', icon: Crown, color: 'text-yellow-400' },
  vice_president: { label: 'Vice President', icon: Shield, color: 'text-electric-ice-blue' },
  treasurer: { label: 'Treasurer', icon: Briefcase, color: 'text-cool-mint', unlocks: 'Unlocks: Create Dues' },
  secretary: { label: 'Secretary', icon: Calendar, color: 'text-soft-amber' },
  risk_manager: { label: 'Risk Manager', icon: Shield, color: 'text-signal-red', unlocks: 'Unlocks: Document Vault' },
  social_chair: { label: 'Social Chair', icon: Users, color: 'text-purple-400' },
  rush_chair: { label: 'Rush Chair', icon: Users, color: 'text-pink-400' },
};

const OfficerCard = ({ officer, availableTitles, onTitleChange }) => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleTitleChange = async (newTitle) => {
    setSaving(true);
    await onTitleChange(officer.membership_id, newTitle === 'none' ? null : newTitle);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const currentTitleConfig = officer.officer_title ? OFFICER_TITLE_CONFIG[officer.officer_title] : null;

  return (
    <div className="flex items-center justify-between p-4 bg-obsidian/50 rounded-xl border border-white/[0.06]">
      <div className="flex items-center gap-4">
        {officer.avatar_url ? (
          <img src={officer.avatar_url} alt={officer.display_name} className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-electric-ice-blue/20 flex items-center justify-center">
            <Users className="w-5 h-5 text-electric-ice-blue" />
          </div>
        )}
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-cloud-white text-sm">{officer.display_name}</p>
            <BadgePill variant={officer.role === 'owner' ? 'amber' : 'blue'} size="sm">{officer.role}</BadgePill>
          </div>
          {currentTitleConfig?.unlocks && <p className="text-xs text-cool-mint">{currentTitleConfig.unlocks}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <SavingIndicator saving={saving} saved={saved} />
        <Select value={officer.officer_title || 'none'} onValueChange={handleTitleChange} disabled={saving}>
          <SelectTrigger className="w-40 bg-graphite border-white/[0.08] text-cloud-white text-sm">
            <SelectValue placeholder="Select title" />
          </SelectTrigger>
          <SelectContent className="bg-graphite border-white/[0.08]">
            <SelectItem value="none" className="text-muted-text">No Title</SelectItem>
            {availableTitles.map((title) => {
              const config = OFFICER_TITLE_CONFIG[title];
              return (
                <SelectItem key={title} value={title} className="text-cloud-white">
                  {config?.label || title}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

const GeneralTab = ({ organization, session, onRefresh }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [ageVerificationKeys, setAgeVerificationKeys] = useState({ public_key: '', secret: '' });
  const [keySaved, setKeySaved] = useState(false);

  const fetchSettings = useCallback(async () => {
    if (!organization?.id || !session?.access_token) return;
    try {
      const res = await fetch(`${API_URL}/api/organizations/${organization.id}/chapter-settings`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  }, [organization?.id, session?.access_token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveOrgField = async (field, value) => {
    try {
      const res = await fetch(`${API_URL}/api/organizations/${organization.id}/chapter-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ [field]: value }),
      });
      if (res.ok) {
        setSettings((prev) => ({ ...prev, organization: { ...prev.organization, [field]: value } }));
        if (onRefresh) onRefresh();
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const saveSocialLink = async (platform, value) => {
    const currentLinks = settings?.organization?.social_links || {};
    const newLinks = { ...currentLinks, [platform]: value };
    if (!value) delete newLinks[platform];
    await saveOrgField('social_links', newLinks);
  };

  const updateOfficerTitle = async (membershipId, title) => {
    try {
      const res = await fetch(`${API_URL}/api/organizations/${organization.id}/officers/${membershipId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ officer_title: title }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSettings((prev) => ({
          ...prev,
          officers: prev.officers.map((o) => o.membership_id === membershipId ? { ...o, officer_title: title } : o),
        }));
        if (data.unlocked_permissions?.length > 0) {
          toast.success(`Unlocked: ${data.unlocked_permissions.join(', ')}`);
        }
      } else {
        toast.error(data.detail || 'Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    const reader = new FileReader();
    reader.onload = async (event) => {
      await saveOrgField('logo_url', event.target?.result);
      setUploadingLogo(false);
      toast.success('Logo updated');
    };
    reader.readAsDataURL(file);
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const res = await fetch(`${API_URL}/api/age-verification/test-connection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ organization_id: organization.id }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('PassportReader connection successful!');
      } else {
        toast.error(data.message || 'Connection failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSaveAgeVerificationKeys = async () => {
    try {
      const res = await fetch(`${API_URL}/api/age-verification/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          organization_id: organization.id,
          public_key: ageVerificationKeys.public_key,
          secret: ageVerificationKeys.secret,
        }),
      });
      if (res.ok) {
        setKeySaved(true);
        setAgeVerificationKeys(prev => ({ ...prev, secret: '' }));
        toast.success('Age verification keys saved');
        setTimeout(() => setKeySaved(false), 2000);
      } else {
        toast.error('Failed to save keys');
      }
    } catch (error) {
      toast.error('Failed to save keys');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-electric-ice-blue animate-spin" />
      </div>
    );
  }

  const org = settings?.organization || {};
  const officers = settings?.officers || [];
  const availableTitles = settings?.available_titles || [];

  return (
    <div className="space-y-6">
      {/* Organization Profile */}
      <SettingsSection title="Organization Profile" description="Basic chapter information" icon={Building2}>
        <div className="space-y-6">
          {/* Logo */}
          <div className="space-y-3">
            <Label className="text-soft-silver">Chapter Logo</Label>
            <div className="flex items-center gap-4">
              {org.logo_url ? (
                <img src={org.logo_url} alt={org.name} className="w-16 h-16 rounded-xl object-cover border border-white/[0.1]" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-electric-ice-blue/10 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-electric-ice-blue" />
                </div>
              )}
              <label className="cursor-pointer">
                <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" disabled={uploadingLogo} />
                <Button type="button" variant="outline" className="border-white/[0.08]" disabled={uploadingLogo} asChild>
                  <span>{uploadingLogo ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</> : <><ImagePlus className="w-4 h-4 mr-2" />Change Logo</>}</span>
                </Button>
              </label>
            </div>
          </div>
          <AutoSaveInput label="Organization Name" value={org.name} onSave={(v) => saveOrgField('name', v)} placeholder="Enter name" />
          <AutoSaveInput label="Description" value={org.description} onSave={(v) => saveOrgField('description', v)} placeholder="Brief description" multiline />
        </div>
      </SettingsSection>

      {/* Social Links */}
      <SettingsSection title="Social Links" description="Connect your social media" icon={Link2}>
        <div className="space-y-4">
          <AutoSaveInput label="Website" value={org.social_links?.website || ''} onSave={(v) => saveSocialLink('website', v)} placeholder="https://yourwebsite.com" type="url" />
          <AutoSaveInput label="Instagram" value={org.social_links?.instagram || ''} onSave={(v) => saveSocialLink('instagram', v)} placeholder="@yourhandle" />
          <AutoSaveInput label="Twitter" value={org.social_links?.twitter || ''} onSave={(v) => saveSocialLink('twitter', v)} placeholder="@yourhandle" />
        </div>
      </SettingsSection>

      {/* Officer Titles */}
      <SettingsSection title="Officer Titles" description="Assign positions to leadership. Treasurer unlocks Dues, Risk Manager unlocks Documents." icon={Crown}>
        <div className="space-y-3">
          {officers.length === 0 ? (
            <p className="text-muted-text text-sm text-center py-6">No officers found. Add admins first.</p>
          ) : (
            officers.map((officer) => (
              <OfficerCard key={officer.membership_id} officer={officer} availableTitles={availableTitles} onTitleChange={updateOfficerTitle} />
            ))
          )}
        </div>
      </SettingsSection>

      {/* Age Verification (PassportReader) */}
      <SettingsSection title="Age Verification" description="Configure PassportReader API for ID verification" icon={Key}>
        <div className="space-y-4">
          <div className="p-3 bg-soft-amber/10 rounded-lg border border-soft-amber/20">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-soft-amber mt-0.5" />
              <p className="text-sm text-soft-amber">API keys are stored securely. The secret is never exposed after saving.</p>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-soft-silver">Public Key</Label>
            <Input
              value={ageVerificationKeys.public_key}
              onChange={(e) => setAgeVerificationKeys(prev => ({ ...prev, public_key: e.target.value }))}
              placeholder="Enter public key"
              className="bg-obsidian border-white/[0.08] text-cloud-white"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-soft-silver">Secret Key</Label>
            <Input
              type="password"
              value={ageVerificationKeys.secret}
              onChange={(e) => setAgeVerificationKeys(prev => ({ ...prev, secret: e.target.value }))}
              placeholder={keySaved ? '••••••••' : 'Enter secret key'}
              className="bg-obsidian border-white/[0.08] text-cloud-white"
            />
          </div>
          <div className="flex gap-3">
            <Button onClick={handleSaveAgeVerificationKeys} disabled={!ageVerificationKeys.public_key} className="bg-electric-ice-blue text-white">
              Save Keys
            </Button>
            <Button onClick={handleTestConnection} variant="outline" className="border-white/[0.08]" disabled={testingConnection}>
              {testingConnection ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Testing...</> : <><TestTube className="w-4 h-4 mr-2" />Test Connection</>}
            </Button>
          </div>
        </div>
      </SettingsSection>
    </div>
  );
};

// ==================== PERMISSIONS TAB ====================

const TABS_CONFIG = [
  { key: 'events', label: 'Events', icon: Calendar },
  { key: 'org_management', label: 'Org Management', icon: Building2 },
  { key: 'finance', label: 'Finance', icon: DollarSign },
  { key: 'communications', label: 'Communications', icon: MessageSquare },
];

const ROLE_LABELS = {
  admin: 'Admin',
  event_manager: 'Event Mgr',
  door_staff: 'Door Staff',
  member: 'Member',
};

const PermissionRow = ({ feature, roles, onToggle, savingKey }) => (
  <div className="flex items-center py-3 border-b border-white/[0.04] last:border-0">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-cloud-white">{feature.label}</p>
      <p className="text-xs text-muted-text">{feature.description}</p>
    </div>
    <div className="flex items-center gap-4 ml-4">
      {Object.entries(feature.roles).map(([role, config]) => {
        const isSaving = savingKey === `${feature.key}_${role}`;
        return (
          <div key={role} className="flex flex-col items-center w-20">
            {config.locked ? (
              <div className="relative group">
                <div className={cn("w-9 h-5 rounded-full flex items-center justify-center", config.enabled ? "bg-cool-mint/20" : "bg-white/[0.05]")}>
                  <Lock className={cn("w-3 h-3", config.enabled ? "text-cool-mint" : "text-muted-text")} />
                </div>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-obsidian rounded text-xs text-soft-silver whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                  {config.lock_reason}
                </div>
              </div>
            ) : (
              <div className="relative">
                <Switch checked={config.enabled} onCheckedChange={(checked) => onToggle(feature.key, role, checked)} disabled={isSaving} />
                {isSaving && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-3 h-3 text-electric-ice-blue animate-spin" /></div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

const PermissionsTab = ({ organization, session }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [activeCategory, setActiveCategory] = useState('events');

  const fetchConfig = useCallback(async () => {
    if (!organization?.id || !session?.access_token) return;
    try {
      const res = await fetch(`${API_URL}/api/permissions/config/${organization.id}`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      if (res.ok) {
        setConfig(await res.json());
      }
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
    } finally {
      setLoading(false);
    }
  }, [organization?.id, session?.access_token]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleToggle = async (feature, role, enabled) => {
    const key = `${feature}_${role}`;
    setSavingKey(key);
    try {
      const res = await fetch(`${API_URL}/api/permissions/config/${organization.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ feature, role, enabled }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setConfig((prev) => {
          const newConfig = { ...prev };
          Object.keys(newConfig.categories).forEach((cat) => {
            newConfig.categories[cat].features = newConfig.categories[cat].features.map((f) => {
              if (f.key === feature) {
                return { ...f, roles: { ...f.roles, [role]: { ...f.roles[role], enabled } } };
              }
              return f;
            });
          });
          return newConfig;
        });
        toast.success('Permission updated');
      } else {
        toast.error(data.detail || 'Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-electric-ice-blue animate-spin" /></div>;
  }

  if (!config) {
    return <EmptyState icon="alert-circle" title="Failed to Load" description="Could not load permissions" actionLabel="Retry" onAction={fetchConfig} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 p-4 bg-electric-ice-blue/5 rounded-lg border border-electric-ice-blue/20">
        <Info className="w-5 h-5 text-electric-ice-blue flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="text-cloud-white font-medium">Navigation Permissions</p>
          <p className="text-soft-silver mt-1">
            Toggle sidebar items on/off per role. <span className="text-cool-mint">Owner always has full access</span>, 
            <span className="text-signal-red"> Door Staff cannot access dues/financials</span>.
          </p>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 border-b border-white/[0.06] pb-2">
        {TABS_CONFIG.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveCategory(tab.key)}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors",
                activeCategory === tab.key ? "bg-electric-ice-blue/10 text-electric-ice-blue" : "text-soft-silver hover:text-cloud-white"
              )}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Permission Table */}
      <div className="bg-graphite/50 rounded-xl border border-white/[0.06] overflow-hidden">
        <div className="flex items-center py-2 px-4 border-b border-white/[0.06] bg-white/[0.02]">
          <div className="flex-1"><span className="text-xs font-medium text-soft-silver uppercase">Feature</span></div>
          <div className="flex items-center gap-4 ml-4">
            {config.roles.map((role) => (
              <div key={role} className="w-20 text-center"><span className="text-xs font-medium text-soft-silver uppercase">{ROLE_LABELS[role]}</span></div>
            ))}
          </div>
        </div>
        <div className="px-4">
          {config.categories[activeCategory]?.features.map((feature) => (
            <PermissionRow key={feature.key} feature={feature} roles={config.roles} onToggle={handleToggle} savingKey={savingKey} />
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== NOTIFICATIONS TAB ====================

const NOTIFICATION_OPTIONS = [
  { key: 'dues_reminder_7_days', label: 'Dues reminder (7 days before)', description: 'Send email 7 days before due date', icon: Clock },
  { key: 'dues_reminder_1_day', label: 'Dues reminder (1 day before)', description: 'Send email 1 day before due date', icon: Clock },
  { key: 'dues_reminder_due_date', label: 'Dues reminder (on due date)', description: 'Send email on the due date', icon: Clock },
  { key: 'document_signature_request', label: 'Document signature requests', description: 'Email when signature required', icon: FileText },
  { key: 'announcement_emails', label: 'Announcement emails', description: 'Email members for new announcements', icon: MessageSquare },
  { key: 'event_reminder_emails', label: 'Event reminders', description: 'Email attendees before events', icon: Calendar },
];

const NotificationToggle = ({ option, enabled, onToggle, saving }) => (
  <div className="flex items-center justify-between p-4 bg-obsidian/50 rounded-xl border border-white/[0.06]">
    <div className="flex items-center gap-4">
      <div className="w-10 h-10 rounded-lg bg-electric-ice-blue/10 flex items-center justify-center">
        <option.icon className="w-5 h-5 text-electric-ice-blue" />
      </div>
      <div>
        <p className="text-sm font-medium text-cloud-white">{option.label}</p>
        <p className="text-xs text-muted-text">{option.description}</p>
      </div>
    </div>
    <div className="flex items-center gap-3">
      {saving && <Loader2 className="w-4 h-4 text-electric-ice-blue animate-spin" />}
      <Switch checked={enabled} onCheckedChange={(checked) => onToggle(option.key, checked)} disabled={saving} />
    </div>
  </div>
);

const NotificationsTab = ({ organization, session }) => {
  const [preferences, setPreferences] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const fetchPreferences = useCallback(async () => {
    if (!organization?.id || !session?.access_token) return;
    try {
      const res = await fetch(`${API_URL}/api/organizations/${organization.id}/notification-preferences`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPreferences(data.preferences || {});
      }
    } catch (error) {
      console.error('Failed to fetch notification preferences:', error);
    } finally {
      setLoading(false);
    }
  }, [organization?.id, session?.access_token]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const handleToggle = async (key, enabled) => {
    setSavingKey(key);
    const newPrefs = { ...preferences, [key]: enabled };
    
    try {
      const res = await fetch(`${API_URL}/api/organizations/${organization.id}/notification-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ preferences: newPrefs }),
      });
      if (res.ok) {
        setPreferences(newPrefs);
        toast.success('Notification setting updated');
      } else {
        toast.error('Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-electric-ice-blue animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 p-4 bg-electric-ice-blue/5 rounded-lg border border-electric-ice-blue/20">
        <Mail className="w-5 h-5 text-electric-ice-blue flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="text-cloud-white font-medium">Email Notifications</p>
          <p className="text-soft-silver mt-1">Configure automated email notifications for your organization.</p>
        </div>
      </div>

      <div className="space-y-3">
        {NOTIFICATION_OPTIONS.map((option) => (
          <NotificationToggle
            key={option.key}
            option={option}
            enabled={preferences[option.key] ?? true}
            onToggle={handleToggle}
            saving={savingKey === option.key}
          />
        ))}
      </div>
    </div>
  );
};

// ==================== MAIN SETTINGS PAGE ====================

export const SettingsPage = () => {
  const { organization, session, refreshOrganization } = useAuth();
  const { hasOrgRole } = usePermissions();
  const isOwner = hasOrgRole(['owner']);
  const [activeTab, setActiveTab] = useState('general');

  if (!isOwner) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <EmptyState icon="lock" title="Access Restricted" description="Only organization owners can access settings." />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Manage organization settings, permissions, and notifications" icon={Settings} />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-graphite/50 border border-white/[0.06] p-1 rounded-lg">
          <TabsTrigger
            value="general"
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-md transition-colors",
              "data-[state=active]:bg-electric-ice-blue/10 data-[state=active]:text-electric-ice-blue",
              "data-[state=inactive]:text-soft-silver"
            )}
          >
            <Building2 className="w-4 h-4" />
            General
          </TabsTrigger>
          <TabsTrigger
            value="permissions"
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-md transition-colors",
              "data-[state=active]:bg-electric-ice-blue/10 data-[state=active]:text-electric-ice-blue",
              "data-[state=inactive]:text-soft-silver"
            )}
          >
            <Shield className="w-4 h-4" />
            Permissions
          </TabsTrigger>
          <TabsTrigger
            value="notifications"
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-md transition-colors",
              "data-[state=active]:bg-electric-ice-blue/10 data-[state=active]:text-electric-ice-blue",
              "data-[state=inactive]:text-soft-silver"
            )}
          >
            <Bell className="w-4 h-4" />
            Notifications
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <GeneralTab organization={organization} session={session} onRefresh={refreshOrganization} />
        </TabsContent>
        <TabsContent value="permissions">
          <PermissionsTab organization={organization} session={session} />
        </TabsContent>
        <TabsContent value="notifications">
          <NotificationsTab organization={organization} session={session} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
